import {
  UserProfile,
  ShotSituation,
  ShotAdvice,
  SwingAnalysis,
  EquipmentRecommendation,
  WeatherConditions,
} from "../types";
import { API_CONFIG } from "../config/api";
import { CRISIS_HELP_REPLY, userTextRequestsCrisisHelp } from "../utils/aiSafety";
import {
  profileToFitting,
  generateCompleteFitting, 
  generateFittingSummaryForAI,
  getOptimalDriverLoft,
  FittingProfile
} from "../utils/fittingEngine";

// SECURITY: All AI calls route through /api/openai-chat serverless endpoint.
// This prevents client-side API key exposure.

export interface AIDistanceRequest {
  userProfile: UserProfile;
  knownDistances: { [club: string]: number };
  conditions?: {
    temperature: number;
    wind: string;
    elevation: number;
  };
}

export interface AIShotAdviceRequest {
  userProfile: UserProfile;
  situation: ShotSituation;
  targetDistance: number;
  availableClubs: string[];
  // Club distances for accurate recommendations
  clubDistances?: { club: string; distance: number }[];
  // Detailed wind conditions for accurate club selection
  windConditions?: {
    speed: number; // mph
    direction: "headwind" | "tailwind" | "crosswind";
    description: string;
  };
  elevationChange?: number; // feet (+positive = uphill, -negative = downhill)
}

export interface AISwingAnalysisRequest {
  userProfile: UserProfile;
  ballFlight: string;
  missPatterns: string[];
  contactIssues: string[];
  goals: string[];
}

export interface AIEquipmentRequest {
  userProfile: UserProfile;
  budget: string;
  priorities: string[];
  currentIssues: string[];
}

export interface DetailedClubRecommendation {
  brand: string;
  model: string;
  category: string;
  specs: {
    loft: string;
    shaft: string;
    length?: string;
    grip?: string;
    faceAngle?: string;
    lie?: string;
    adjustability?: string;
  };
  reasoning: string;
  priceRange: string;
  alternatives: string[];
  specificAdjustments?: {
    loftSleeve?: string;
    lieAngle?: string;
    faceAngle?: string;
    weight?: string;
    grip?: string;
  };
  detailedSpecs?: {
    shaftDetails?: {
      model: string;
      weight: string;
      torque: string;
      launch: string;
      spin: string;
    };
    gripDetails?: {
      model: string;
      size: string;
      material: string;
      wraps?: string;
    };
    technologyHighlights?: string[];
  };
}

class AIService {
  private async makeAIRequest(
    prompt: string,
    systemMessage: string,
  ): Promise<string> {
    try {
      if (userTextRequestsCrisisHelp(`${prompt}\n${systemMessage}`)) {
        return CRISIS_HELP_REPLY;
      }
      // SECURITY: ALL calls go through /api/openai-chat (never direct browser calls)
      // This prevents API key exposure following ChatGPT's recommended pattern
      const resp = await fetch('/api/openai-chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt,
          systemMessage,
          model: API_CONFIG.openai.model,
          temperature: API_CONFIG.openai.temperature,
          maxTokens: API_CONFIG.openai.maxTokens,
        }),
      });
      
      if (!resp.ok) {
        const errorData = await resp.json().catch(() => ({ error: 'Unknown error' }));
        const errorMessage = errorData.error || `HTTP ${resp.status}`;
        throw new Error(`API error: ${errorMessage}`);
      }
      
      const data = await resp.json();
      return data.text || "";
    } catch (error) {
      console.error("AI Service Error:", error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error("Error details:", {
        message: errorMessage,
        stack: error instanceof Error ? error.stack : undefined,
      });
      
      // Provide helpful error message for localhost
      if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        if (errorMessage.includes('fetch') || errorMessage.includes('Failed to fetch') || errorMessage.includes('404')) {
          throw new Error("AI API route not found. In localhost, use 'vercel dev' instead of 'npm start' to run API routes. Install Vercel CLI: npm i -g vercel");
        }
      }

      if (error instanceof Error) {
        if (error.message.includes("401") || error.message.includes("Unauthorized")) {
          throw new Error("OpenAI API key is invalid or expired. Please check your configuration.");
        } else if (error.message.includes("429")) {
          throw new Error("OpenAI API rate limit exceeded. Please try again in a moment.");
        } else if (error.message.includes("404") && error.message.includes("model")) {
          throw new Error("The AI model is not accessible with your API key. Please check your OpenAI account and API key permissions.");
        } else if (error.message.includes("network") || error.message.includes("fetch")) {
          throw new Error("Network error. Please check your internet connection and try again.");
        } else {
          throw new Error(`AI service error: ${error.message}`);
        }
      }

      throw new Error("AI service temporarily unavailable. Please try again later.");
    }
  }

  async getAIDistanceCalculation(
    request: AIDistanceRequest,
  ): Promise<{ [club: string]: number }> {
    const systemMessage = `You are a PGA-certified golf instructor and club fitting expert with 20+ years of experience. You specialize in calculating accurate club distances based on player characteristics and known distances.

Your expertise includes:
- Understanding how swing speed, tempo, and ball flight affect distance
- Knowledge of standard club lofts and gapping
- Ability to account for weather and course conditions
- Experience with different player types and skill levels`;

    const prompt = `Based on the following golfer profile and known distances, calculate accurate distances for all clubs:

GOLFER PROFILE:
- Handicap: ${request.userProfile.handicap || "Not specified"}
- Swing Speed: ${request.userProfile.swingSpeed} mph
- Age: ${request.userProfile.age}
- Height: ${request.userProfile.height} inches
- Ball Flight: ${request.userProfile.ballFlight}
- Skill Level: ${request.userProfile.skillLevel}

KNOWN DISTANCES:
${Object.entries(request.knownDistances)
  .map(([club, distance]) => `${club}: ${distance} yards`)
  .join("\n")}

CONDITIONS:
${request.conditions ? `Temperature: ${request.conditions.temperature}°F, Wind: ${request.conditions.wind}, Elevation: ${request.conditions.elevation}ft` : "Standard conditions"}

Please provide distances for all standard clubs (Driver, 3W, 5W, 3H-5H, 3I-9I, PW, GW, SW, LW) in the following JSON format:
{"driver": 250, "3-wood": 230, ...}

Consider the golfer's characteristics and use your expertise to provide realistic, achievable distances.`;

    try {
      const response = await this.makeAIRequest(prompt, systemMessage);
      const jsonMatch = response.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        return JSON.parse(jsonMatch[0]);
      }
      throw new Error("Invalid AI response format");
    } catch (error) {
      console.error("AI Distance Calculation Error:", error);
      throw error;
    }
  }

  async getAIShotAdvice(request: AIShotAdviceRequest): Promise<ShotAdvice> {
    // Calculate wind impact on club selection
    const windImpact = this.calculateWindImpact(request.windConditions, request.targetDistance);
    const elevationImpact = this.calculateElevationImpact(request.elevationChange, request.targetDistance);
    
    // Calculate EFFECTIVE PLAYING DISTANCE (target adjusted for wind + elevation)
    let effectiveDistance = request.targetDistance;
    
    // Apply wind adjustment if speed > 0
    if (request.windConditions?.speed && request.windConditions.speed > 0) {
      if (request.windConditions.direction === 'headwind') {
        effectiveDistance += windImpact.yardsAffected;
      } else if (request.windConditions.direction === 'tailwind') {
        effectiveDistance -= windImpact.yardsAffected;
      }
    }
    
    // Elevation: Uphill (positive) plays longer, downhill (negative) plays shorter
    // Rule of thumb: ~1 yard per 3 feet of elevation change (0.33 multiplier)
    if (request.elevationChange) {
      effectiveDistance += Math.round(request.elevationChange * 0.33);
    }
    
    // Log the calculation summary
    const elevAdj = request.elevationChange ? Math.round(request.elevationChange * 0.33) : 0;
    console.log('[ShotAdvisor] Effective distance:', effectiveDistance, 
      `(${request.targetDistance} target + ${windImpact.yardsAffected} wind + ${elevAdj} elev from ${request.elevationChange || 0}ft)`);
    
    
    const systemMessage = `You are a highly experienced PGA Tour caddie and golf instructor. You provide precise, situational golf advice based on course conditions, player characteristics, and shot requirements.

Your expertise includes:
- Reading course conditions and their impact on ball flight
- Understanding how different lies affect club selection and setup
- Knowledge of shot execution techniques for various situations
- Experience with players of all skill levels

IMPORTANT — CLUB SELECTION:
- You will receive an EFFECTIVE PLAYING DISTANCE that already accounts for wind and elevation.
- DO NOT add your own wind or elevation adjustments on top. The effective distance is the number to match against the golfer's club distances.
- Simply find the club whose carry distance is closest to the effective distance.
- For crosswind: adjust aim, NOT club selection.

CLUB SELECTION MUST BE BASED ON THE GOLFER'S ACTUAL DISTANCES, NOT GENERIC DISTANCES.`;

    // Build wind context for prompt
    const windContext = request.windConditions && request.windConditions.speed > 0
      ? `
WIND CONDITIONS (CRITICAL FOR CLUB SELECTION):
- Wind Speed: ${request.windConditions.speed} mph
- Wind Direction: ${request.windConditions.direction.toUpperCase()}
- Estimated Impact: ${windImpact.description}
- Adjustment Needed: ${windImpact.adjustment}`
      : `
WIND CONDITIONS: No significant wind`;

    const elevationContext = request.elevationChange && request.elevationChange !== 0
      ? `
ELEVATION CHANGE:
- Change: ${request.elevationChange > 0 ? '+' : ''}${request.elevationChange} feet (${request.elevationChange > 0 ? 'uphill' : 'downhill'})
- Impact: ${elevationImpact.description}
- Adjustment: ${elevationImpact.adjustment}`
      : '';

    // Build club distances section - THIS IS CRITICAL for proper club selection
    // Sort by distance for the AI to easily find closest match
    const sortedClubDistances = request.clubDistances && request.clubDistances.length > 0
      ? [...request.clubDistances].sort((a, b) => (b.distance || 0) - (a.distance || 0))
      : [];
      
    const clubDistancesSection = sortedClubDistances.length > 0
      ? `
⚠️ THIS GOLFER'S ACTUAL CLUB DISTANCES (YOU MUST USE THESE - NOT GENERIC DISTANCES):
${sortedClubDistances.map(cd => `- ${cd.club}: ${cd.distance} yards carry`).join('\n')}

FIND THE CLUB WHOSE CARRY DISTANCE IS CLOSEST TO ${Math.round(effectiveDistance)} YARDS.`
      : '';

    const prompt = `Provide detailed shot advice for this situation:

GOLFER:
- Handicap: ${request.userProfile.handicap || "Not specified"}
- Typical ball flight: ${request.userProfile.ballFlight}
- Swing speed: ${request.userProfile.swingSpeed} mph
- Current issues: ${request.userProfile.currentIssues.join(", ") || "none"}
${clubDistancesSection}

SHOT SITUATION:
- Target Distance: ${request.targetDistance} yards
- EFFECTIVE PLAYING DISTANCE: ${Math.round(effectiveDistance)} yards (adjusted for wind/elevation)
- Lie: ${request.situation.lie}
- Slope: ${request.situation.slope}
- Pin position: ${request.situation.pin}
- Hazards: ${request.situation.hazards.join(", ") || "none"}
${windContext}${elevationContext}

AVAILABLE CLUBS: ${request.availableClubs.join(", ")}

⚠️ CRITICAL INSTRUCTIONS:
1. The EFFECTIVE PLAYING DISTANCE (${Math.round(effectiveDistance)} yards) ALREADY includes wind and elevation adjustments. DO NOT add further club changes for wind or elevation — just match the effective distance to the golfer's club distances.
2. Your job: pick the club that carries closest to ${Math.round(effectiveDistance)} yards, then advise on setup, aim, and execution.
3. The "clubSelection" explanation should describe why this club fits the effective distance.

Provide advice in JSON format:
{
  "recommendedClub": "7-iron",
  "alternativeClubs": ["6-iron", "8-iron"],
  "adjustments": {
    "clubSelection": "With ${request.targetDistance} yards playing as ${Math.round(effectiveDistance)} yards${request.windConditions && request.windConditions.speed > 0 ? ` (${windImpact.description})` : ''}${request.elevationChange ? `, elevation ${request.elevationChange > 0 ? '+' : ''}${request.elevationChange}ft` : ''}, your [club] at [X yards] is the best match.",
    "ballPosition": "ball position adjustment",
    "stance": "stance modification",
    "swing": "swing adjustment",
    "aim": "aiming adjustment"
  },
  "tips": ["tip 1", "tip 2", "tip 3"]
}

Be specific and actionable in your advice.`;

    try {
      const response = await this.makeAIRequest(prompt, systemMessage);
      const jsonMatch = response.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        const aiAdvice = JSON.parse(jsonMatch[0]);
        
        // POST-PROCESSING: Validate and override AI's club selection to match user's actual distances
        if (sortedClubDistances.length > 0) {
          const bestClub = this.findBestClubForDistance(sortedClubDistances, effectiveDistance);
          const bestClubDistance = sortedClubDistances.find(cd => cd.club === bestClub)?.distance || 0;
          
          // ALWAYS use the best club from user's actual distances
          if (bestClub && bestClubDistance > 0) {
            aiAdvice.recommendedClub = bestClub;
            aiAdvice.adjustments.clubSelection = `For ${Math.round(effectiveDistance)} yards (effective), your ${bestClub.replace(/-/g, ' ')} at ${bestClubDistance} yards is the closest match.`;
          }
          
          // POST-PROCESS ALTERNATIVES: Ensure they are adjacent to the recommended club
          // This prevents nonsense like suggesting a 6-iron as an alternative to a 9-iron
          if (aiAdvice.alternativeClubs) {
            const recommendedClub = aiAdvice.recommendedClub;
            const recommendedIndex = sortedClubDistances.findIndex(cd => cd.club === recommendedClub);
            
            if (recommendedIndex >= 0) {
              const adjacentAlternatives: string[] = [];
              // Get the club before (longer distance) - this is one club "up"
              if (recommendedIndex > 0) {
                adjacentAlternatives.push(sortedClubDistances[recommendedIndex - 1].club);
              }
              // Get the club after (shorter distance) - this is one club "down"
              if (recommendedIndex < sortedClubDistances.length - 1) {
                adjacentAlternatives.push(sortedClubDistances[recommendedIndex + 1].club);
              }
              aiAdvice.alternativeClubs = adjacentAlternatives;
            }
          }
        }
        
        console.log('[ShotAdvisor] Recommending:', aiAdvice.recommendedClub, 'for', effectiveDistance, 'yards effective');
        return aiAdvice;
      }
      throw new Error("Invalid AI response format");
    } catch (error) {
      console.error("AI Shot Advice Error:", error);
      throw error;
    }
  }
  
  /**
   * Find the best club for a given distance from the golfer's actual distances
   */
  private findBestClubForDistance(
    clubDistances: { club: string; distance: number }[],
    targetDistance: number
  ): string {
    if (clubDistances.length === 0) return '';
    
    // Find club with minimum distance delta
    let bestClub = clubDistances[0].club;
    let minDelta = Math.abs(clubDistances[0].distance - targetDistance);
    
    for (const cd of clubDistances) {
      const delta = Math.abs(cd.distance - targetDistance);
      if (delta < minDelta) {
        minDelta = delta;
        bestClub = cd.club;
      }
    }
    
    return bestClub;
  }

  /**
   * Calculate wind impact on shot distance
   * 
   * STEPPED THRESHOLDS (aligned with golfer intuition):
   * - 0-7 mph:  No club change, swing/trajectory guidance only
   * - 8-15 mph: ~1 club adjustment (8-12 yards)
   * - 16-20 mph: ~2 club adjustment (15-20 yards)
   * - 20+ mph: Larger adjustment (22+ yards)
   */
  private calculateWindImpact(
    windConditions?: { speed: number; direction: "headwind" | "tailwind" | "crosswind"; description: string },
    targetDistance?: number
  ): { description: string; adjustment: string; yardsAffected: number } {
    if (!windConditions || windConditions.speed === 0) {
      return { description: "No wind impact", adjustment: "None", yardsAffected: 0 };
    }

    const { speed, direction } = windConditions;
    let yardsAffected = 0;
    let description = "";
    let adjustment = "";

    if (direction === "headwind") {
      // GRADUATED HEADWIND THRESHOLDS — every wind level produces a real adjustment
      // Uses smooth scaling: ~1 yard per mph for light, tapering off at higher speeds
      if (speed <= 5) {
        // Light breeze: small but noticeable — roughly 3-5 yards
        yardsAffected = Math.round(speed * 0.8); // 5mph → 4 yards
        description = `Light ${speed} mph headwind — ball may carry ~${yardsAffected} yards shorter`;
        adjustment = "Likely same club. Commit to a smooth, full swing — solid contact matters more than clubbing up here.";
      } else if (speed <= 10) {
        // Moderate breeze: roughly half a club — 5-8 yards
        yardsAffected = Math.round(4 + (speed - 5) * 0.8); // 6-8mph → 5-8 yards
        description = `Moderate ${speed} mph headwind — ball will carry ~${yardsAffected} yards shorter`;
        adjustment = "Consider one club more. Grip down slightly and make a smooth swing — don't force it.";
      } else if (speed <= 15) {
        // Firm wind: one club — 9-12 yards
        yardsAffected = Math.round(8 + (speed - 10) * 0.8); // 9-12 yards
        description = `Firm ${speed} mph headwind — ball will carry ~${yardsAffected} yards shorter`;
        adjustment = "Take one extra club (e.g., 6-iron instead of 7-iron). Keep ball flight low.";
      } else if (speed <= 20) {
        // Strong wind: one to one-and-a-half clubs — 13-17 yards
        yardsAffected = Math.round(12 + (speed - 15) * 1.0); // 13-17 yards
        description = `Strong ${speed} mph headwind — ball will carry ~${yardsAffected} yards shorter`;
        adjustment = "Take one to two extra clubs. Keep the ball low — punch or knockdown if possible.";
      } else {
        // Severe wind: two clubs — 18+ yards
        yardsAffected = Math.round(17 + (speed - 20) * 1.2); // 18+ yards
        description = `Severe ${speed} mph headwind — ball will carry ~${yardsAffected} yards shorter`;
        adjustment = "Two clubs more and play a knockdown. Prioritize control over distance.";
      }
    } else if (direction === "tailwind") {
      // GRADUATED TAILWIND THRESHOLDS — roughly 60% of headwind impact
      if (speed <= 5) {
        // Light tailwind: small gain — 2-3 yards
        yardsAffected = Math.round(speed * 0.5); // 5mph → 3 yards
        description = `Light ${speed} mph tailwind — ball may carry ~${yardsAffected} yards farther`;
        adjustment = "Same club. Ball may roll out a bit more on landing.";
      } else if (speed <= 10) {
        // Moderate tailwind: roughly half a club shorter — 4-6 yards
        yardsAffected = Math.round(3 + (speed - 5) * 0.6); // 4-6 yards
        description = `Moderate ${speed} mph tailwind — ball will carry ~${yardsAffected} yards farther`;
        adjustment = "Consider one less club. Watch for extra roll — fly it short of the pin.";
      } else if (speed <= 15) {
        // Firm tailwind: one club less — 7-9 yards
        yardsAffected = Math.round(6 + (speed - 10) * 0.6); // 7-9 yards
        description = `Firm ${speed} mph tailwind — ball will carry ~${yardsAffected} yards farther`;
        adjustment = "One less club. Reduced spin means extra roll, so plan landing short.";
      } else if (speed <= 20) {
        // Strong tailwind: one to one-and-a-half clubs less — 10-13 yards
        yardsAffected = Math.round(9 + (speed - 15) * 0.8); // 10-13 yards
        description = `Strong ${speed} mph tailwind — ball will carry ~${yardsAffected} yards farther`;
        adjustment = "One to two fewer clubs. Higher ball flight will maximize the wind assist but adds roll.";
      } else {
        // Severe tailwind: 14+ yards
        yardsAffected = Math.round(13 + (speed - 20) * 1.0); // 14+ yards
        description = `Severe ${speed} mph tailwind — ball will carry ~${yardsAffected} yards farther`;
        adjustment = "Club down significantly. Be cautious of overshooting the target.";
      }
    } else {
      // Crosswind — affects aim/line, not distance
      if (speed <= 5) {
        description = `Light ${speed} mph crosswind — minor ball drift`;
        adjustment = "Aim 2-3 yards into the wind. Minimal impact on club selection.";
      } else if (speed <= 10) {
        description = `Moderate ${speed} mph crosswind — noticeable ball drift`;
        adjustment = "Aim 5-8 yards into the wind to compensate.";
      } else if (speed <= 15) {
        description = `Firm ${speed} mph crosswind — significant ball drift`;
        adjustment = `Aim 10-15 yards into the wind. Consider a lower trajectory to reduce drift.`;
      } else {
        description = `Strong ${speed} mph crosswind — heavy ball drift`;
        adjustment = `Aim 15-20 yards into the wind. Knockdown shot recommended to reduce lateral movement.`;
      }
      yardsAffected = 0; // Crosswind doesn't change distance, only aim
    }

    return { description, adjustment, yardsAffected };
  }

  /**
   * Calculate elevation impact on shot distance
   */
  private calculateElevationImpact(
    elevationChange?: number,
    targetDistance?: number
  ): { description: string; adjustment: string; yardsAffected: number } {
    if (!elevationChange || elevationChange === 0) {
      return { description: "Flat terrain", adjustment: "None", yardsAffected: 0 };
    }

    // Rough estimate: 1 foot of elevation ≈ 1 yard of impact
    const yardsAffected = Math.abs(elevationChange);
    
    if (elevationChange > 0) {
      // Uphill - ball will fly shorter
      return {
        description: `Uphill shot (+${elevationChange}ft) will play ~${yardsAffected} yards longer`,
        adjustment: yardsAffected >= 10 ? "Club up 1 club" : "Consider clubbing up",
        yardsAffected
      };
    } else {
      // Downhill - ball will fly further
      return {
        description: `Downhill shot (${elevationChange}ft) will play ~${yardsAffected} yards shorter`,
        adjustment: yardsAffected >= 10 ? "Club down 1 club" : "Consider clubbing down",
        yardsAffected
      };
    }
  }

  async getAISwingAnalysis(
    request: AISwingAnalysisRequest,
  ): Promise<SwingAnalysis> {
    const systemMessage = `You are a renowned golf swing coach with expertise in biomechanics and swing analysis. You help golfers identify and fix swing issues through detailed analysis and targeted practice recommendations.

Your expertise includes:
- Understanding the relationship between ball flight and swing mechanics
- Identifying root causes of common swing problems
- Providing specific, actionable corrections
- Creating effective practice routines`;

    const prompt = `Analyze this golfer's swing and provide detailed improvement recommendations:

GOLFER PROFILE:
- Handicap: ${request.userProfile.handicap || "Not specified"}
- Ball flight pattern: ${request.ballFlight}
- Swing speed: ${request.userProfile.swingSpeed} mph
- Swing tempo: ${request.userProfile.swingTempo}
- Age: ${request.userProfile.age}

SWING ISSUES:
- Miss patterns: ${request.missPatterns.join(", ")}
- Contact issues: ${request.contactIssues.join(", ")}
- Goals: ${request.goals.join(", ")}

Provide a comprehensive analysis in JSON format:
{
  "ballFlight": "analysis of current ball flight",
  "issues": ["root cause 1", "root cause 2"],
  "corrections": {
    "grip": ["grip correction 1", "grip correction 2"],
    "stance": ["stance correction 1"],
    "swing": ["swing correction 1", "swing correction 2"],
    "equipment": ["equipment suggestion 1"]
  },
  "drills": [
    {
      "name": "Drill Name",
      "description": "Detailed drill description",
      "frequency": "How often to practice"
    }
  ]
}

Focus on the most impactful changes first and provide specific, actionable advice.`;

    try {
      const response = await this.makeAIRequest(prompt, systemMessage);
      const jsonMatch = response.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        return JSON.parse(jsonMatch[0]);
      }
      throw new Error("Invalid AI response format");
    } catch (error) {
      console.error("AI Swing Analysis Error:", error);
      throw error;
    }
  }

  async getAIEquipmentRecommendations(
    request: AIEquipmentRequest,
  ): Promise<EquipmentRecommendation[]> {
    // Step 1: Convert user profile to fitting profile and get database matches
    const fittingProfile: FittingProfile = profileToFitting(request.userProfile);
    const fittingResults = generateCompleteFitting(fittingProfile);
    
    // Step 2: Generate summaries of top matches from our database
    const driverMatches = generateFittingSummaryForAI(fittingResults.driver, 'Driver');
    const ironMatches = generateFittingSummaryForAI(fittingResults.irons, 'Irons');
    const wedgeMatches = generateFittingSummaryForAI(fittingResults.wedges, 'Wedges');
    
    // Step 3: Calculate optimal specs
    const optimalLoft = getOptimalDriverLoft(fittingProfile.swingSpeed, fittingProfile.attackAngle);
    const optimalFlex = fittingProfile.swingSpeed >= 105 ? 'Stiff' : fittingProfile.swingSpeed >= 90 ? 'Regular' : 'Senior';
    const optimalShaftWeight = fittingProfile.swingSpeed >= 105 ? '70g' : fittingProfile.swingSpeed >= 90 ? '60g' : '50g';
    
    // Map budget to label and price constraint for the AI
    // EXACT CATEGORY-SPECIFIC MARKET PRICING (user-specified tiers)
    const budgetLabels: Record<string, { label: string; constraint: string }> = {
      'budget': { label: 'Value Tier', constraint: 'Drivers $100-200, Fairways/Hybrids $100-180, Iron sets $500-700, Wedges $50-75' },
      'medium': { label: 'Mid-Range', constraint: 'Drivers $200-300, Fairways/Hybrids $180-250, Iron sets $700-1100, Wedges $75-105' },
      'premium': { label: 'Premium', constraint: 'Drivers $300+, Fairways/Hybrids $250+, Iron sets $1100+, Wedges $105+' },
      'no-limit': { label: 'No Limit', constraint: 'Any price range' }
    };
    const budgetInfo = budgetLabels[request.budget] || budgetLabels['medium'];
    
    const systemMessage = `You are a Master Club Fitter with 20+ years of experience at a top club fitting studio. You provide SPECIFIC, DATA-DRIVEN recommendations that feel like a $400 professional fitting session.

CRITICAL RULES:
1. NEVER give generic recommendations - every spec must be tied to the player's specific data
2. USE THE PROVIDED DATABASE MATCHES - these are pre-filtered for this player's budget
3. BUDGET IS A HARD FILTER - You MUST only recommend clubs within the specified price tier. This is non-negotiable.
4. Explain WHY each spec choice matters for THIS golfer specifically
5. Include exact shaft models with weight/flex, exact loft settings, and grip specs
6. Reference their swing speed, ball flight, and handicap in your reasoning`;

    const prompt = `PROFESSIONAL CLUB FITTING FOR THIS GOLFER:

═══════════════════════════════════════════════════════════════
GOLFER PROFILE (Use these EXACT numbers in your recommendations)
═══════════════════════════════════════════════════════════════
• Swing Speed: ${request.userProfile.swingSpeed || fittingProfile.swingSpeed} mph
• Handicap: ${request.userProfile.handicap || 18}
• Ball Flight: ${request.userProfile.ballFlight || 'straight'}
• Height: ${request.userProfile.height}" (${Math.floor((request.userProfile.height || 70) / 12)}'${(request.userProfile.height || 70) % 12}")
• Age: ${request.userProfile.age || 35}
• Swing Tempo: ${request.userProfile.swingTempo || 'medium'}
• Skill Level: ${request.userProfile.skillLevel || 'intermediate'}

═══════════════════════════════════════════════════════════════
CALCULATED OPTIMAL SPECS (Based on their profile)
═══════════════════════════════════════════════════════════════
• Optimal Driver Loft: ${optimalLoft}°
• Recommended Flex: ${optimalFlex}
• Recommended Shaft Weight: ${optimalShaftWeight}

═══════════════════════════════════════════════════════════════
BUDGET CONSTRAINT (MANDATORY - DO NOT IGNORE)
═══════════════════════════════════════════════════════════════
• Budget Tier: ${budgetInfo.label}
• Price Filter: ${budgetInfo.constraint}
⚠️ THIS IS A HARD FILTER. Do NOT recommend any club outside this price range.

═══════════════════════════════════════════════════════════════
FITTING PRIORITIES & ISSUES
═══════════════════════════════════════════════════════════════
• Priorities: ${request.priorities.join(", ") || 'forgiveness, distance'}
• Current Issues: ${request.currentIssues.join(", ") || 'none specified'}

═══════════════════════════════════════════════════════════════
PRE-MATCHED CLUBS FROM OUR DATABASE (Select from these)
═══════════════════════════════════════════════════════════════

${driverMatches}

${ironMatches}

${wedgeMatches}

═══════════════════════════════════════════════════════════════
YOUR TASK: Create PERSONALIZED recommendations using this data
═══════════════════════════════════════════════════════════════

Provide recommendations in JSON format. Each recommendation MUST:
1. ONLY use clubs from the database matches above - these are already filtered for budget
2. Include specific shaft model, weight, and flex based on their ${request.userProfile.swingSpeed || fittingProfile.swingSpeed} mph swing speed
3. Explain how it addresses their ${request.userProfile.ballFlight || 'straight'} ball flight
4. Reference their ${request.userProfile.handicap || 18} handicap in the reasoning
5. Include specific adjustments (loft sleeve, weights, etc.)
6. Ensure priceRange stays within ${budgetInfo.label} tier (${budgetInfo.constraint})

JSON FORMAT:
[
  {
    "category": "driver",
    "recommendations": [
      {
        "brand": "Brand from database",
        "model": "Model from database",
        "matchPercent": 95,
        "specs": {
          "loft": "${optimalLoft}°",
          "shaft": "[Shaft Model] ${optimalShaftWeight} - ${optimalFlex}",
          "length": "45.25 inches",
          "grip": "Golf Pride Tour Velvet 360 - Standard"
        },
        "reasoning": "Based on your ${request.userProfile.swingSpeed || fittingProfile.swingSpeed} mph swing speed and ${request.userProfile.ballFlight || 'straight'} ball flight, the [Model] with ${optimalLoft}° loft optimizes your launch angle for maximum carry. The [Shaft] at ${optimalShaftWeight} provides [benefit] for your ${request.userProfile.swingTempo || 'medium'} tempo.",
        "priceRange": "$XXX-XXX",
        "alternatives": ["Alt 1 from database", "Alt 2 from database"],
        "specificAdjustments": {
          "loftSleeve": "Specific adjustment based on their profile",
          "faceAngle": "Adjustment if needed for ball flight",
          "weight": "Weight setting recommendation"
        },
        "detailedSpecs": {
          "shaftDetails": {
            "model": "Exact shaft model",
            "weight": "${optimalShaftWeight}",
            "torque": "X.X°",
            "launch": "low/mid/high",
            "spin": "low/mid/high"
          },
          "technologyHighlights": ["Tech 1 and how it helps this golfer", "Tech 2"]
        }
      }
    ]
  }
]

Include driver, irons, and wedges. Make every recommendation feel like a $400 professional fitting.`;

    try {
      const response = await this.makeAIRequest(prompt, systemMessage);
      const jsonMatch = response.match(/\[[\s\S]*\]/);
      if (jsonMatch) {
        return JSON.parse(jsonMatch[0]);
      }
      throw new Error("Invalid AI response format");
    } catch (error) {
      console.error("AI Equipment Recommendations Error:", error);
      // Fall back to database-driven recommendations without AI personalization
      return this.generateFallbackRecommendations(fittingResults, fittingProfile, request);
    }
  }
  
  // Fallback to use fitting engine directly without AI - personalized reasoning
  private generateFallbackRecommendations(
    fittingResults: ReturnType<typeof generateCompleteFitting>,
    profile: FittingProfile,
    request: AIEquipmentRequest
  ): EquipmentRecommendation[] {
    const recommendations: EquipmentRecommendation[] = [];
    
    // Helper to build personalized reasoning
    const buildDriverReasoning = (matchReasons: string[]): string => {
      const parts: string[] = [];
      
      // Swing speed context
      if (profile.swingSpeed >= 105) {
        parts.push(`Optimized for your ${profile.swingSpeed} mph swing speed`);
      } else if (profile.swingSpeed >= 95) {
        parts.push(`Matched to your ${profile.swingSpeed} mph driver speed for optimal launch`);
      } else {
        parts.push(`Built to maximize distance at your ${profile.swingSpeed} mph swing speed`);
      }
      
      // Ball flight context
      if (profile.ballFlight === 'slice' || profile.ballFlight === 'fade') {
        parts.push('Draw-bias weighting helps reduce slice spin');
      } else if (profile.ballFlight === 'hook' || profile.ballFlight === 'draw') {
        parts.push('Neutral setup prevents over-drawing');
      }
      
      // Handicap context
      if (profile.handicap <= 10) {
        parts.push('Workability and control for skilled players');
      } else if (profile.handicap <= 20) {
        parts.push(`Designed for ${profile.handicap} handicap golfers`);
      } else {
        parts.push('High MOI for forgiveness on off-center hits');
      }
      
      return parts.join('. ') + '.';
    };
    
    const buildIronReasoning = (matchReasons: string[]): string => {
      const parts: string[] = [];
      
      // Skill level context
      if (profile.handicap <= 8) {
        parts.push(`Tour-level feedback and workability for your ${profile.handicap} handicap`);
      } else if (profile.handicap <= 15) {
        parts.push(`Optimal blend of forgiveness and control for mid-handicappers`);
      } else {
        parts.push(`Maximum forgiveness and distance for improving golfers`);
      }
      
      // Swing speed for shaft
      if (profile.swingSpeed >= 95) {
        parts.push(`Stiff shafts matched to your ${profile.swingSpeed} mph swing`);
      } else if (profile.swingSpeed >= 85) {
        parts.push(`Regular flex for consistent tempo and control`);
      } else {
        parts.push(`Senior/light flex for easier launch`);
      }
      
      return parts.join('. ') + '.';
    };
    
    const buildWedgeReasoning = (matchReasons: string[]): string => {
      const parts: string[] = [];
      parts.push(`Optimized for your ${profile.swingSpeed} mph swing speed`);
      
      if (profile.handicap <= 12) {
        parts.push('Tour-level spin and versatility around the greens');
      } else {
        parts.push('Consistent gapping to fill distance gaps in your bag');
      }
      
      return parts.join('. ') + '.';
    };
    
    // Driver recommendation
    if (fittingResults.driver.length > 0) {
      const topDriver = fittingResults.driver[0];
      const driverRec: EquipmentRecommendation = {
        category: 'driver',
        recommendations: [{
          brand: topDriver.club.brand,
          model: topDriver.club.model,
          specs: {
            loft: topDriver.recommendedLoft ? `${topDriver.recommendedLoft}°` : '10.5°',
            shaft: topDriver.shaft 
              ? `${topDriver.shaft.brand} ${topDriver.shaft.model} ${topDriver.recommendedShaftWeight}g ${topDriver.recommendedFlex}`
              : `${topDriver.recommendedShaftWeight}g ${topDriver.recommendedFlex} Graphite`,
            adjustability: 'Full adjustability'
          },
          reasoning: buildDriverReasoning(topDriver.matchReasons),
          priceRange: topDriver.club.priceRange || '$400-500',
          alternatives: fittingResults.driver.slice(1, 3).map(d => `${d.club.brand} ${d.club.model}`),
          specificAdjustments: topDriver.adjustmentRecommendations,
          detailedSpecs: {
            shaftDetails: topDriver.shaft ? {
              model: `${topDriver.shaft.brand} ${topDriver.shaft.model}`,
              weight: `${topDriver.recommendedShaftWeight}g`,
              torque: `${topDriver.shaft.torque}°`,
              launch: topDriver.shaft.launch,
              spin: topDriver.shaft.spin
            } : undefined,
            technologyHighlights: topDriver.club.keyTechnologies.slice(0, 3)
          }
        }]
      };
      recommendations.push(driverRec);
    }
    
    // Iron recommendation
    if (fittingResults.irons.length > 0) {
      const topIrons = fittingResults.irons[0];
      const ironRec: EquipmentRecommendation = {
        category: 'irons',
        recommendations: [{
          brand: topIrons.club.brand,
          model: topIrons.club.model,
          specs: {
            type: topIrons.club.playerType?.replace('-', ' ') || 'Game Improvement',
            shaft: topIrons.shaft 
              ? `${topIrons.shaft.brand} ${topIrons.shaft.model} ${topIrons.recommendedFlex}`
              : `${topIrons.recommendedFlex} Steel`,
            set: '5-PW'
          },
          reasoning: buildIronReasoning(topIrons.matchReasons),
          priceRange: topIrons.club.priceRange || '$800-1200',
          alternatives: fittingResults.irons.slice(1, 3).map(i => `${i.club.brand} ${i.club.model}`),
          detailedSpecs: {
            shaftDetails: topIrons.shaft ? {
              model: `${topIrons.shaft.brand} ${topIrons.shaft.model}`,
              weight: `${topIrons.recommendedShaftWeight}g`,
              torque: `${topIrons.shaft.torque}°`,
              launch: topIrons.shaft.launch,
              spin: topIrons.shaft.spin
            } : undefined,
            technologyHighlights: topIrons.club.keyTechnologies.slice(0, 3)
          }
        }]
      };
      recommendations.push(ironRec);
    }
    
    // Wedge recommendation
    if (fittingResults.wedges.length > 0) {
      const topWedge = fittingResults.wedges[0];
      const wedgeRec: EquipmentRecommendation = {
        category: 'wedges',
        recommendations: [{
          brand: topWedge.club.brand,
          model: topWedge.club.model,
          specs: {
            lofts: '50°, 54°, 58°',
            shaft: `${topWedge.recommendedFlex} Steel`,
            grind: 'Mid bounce for versatility'
          },
          reasoning: buildWedgeReasoning(topWedge.matchReasons),
          priceRange: topWedge.club.priceRange || '$150-180 each',
          alternatives: fittingResults.wedges.slice(1, 3).map(w => `${w.club.brand} ${w.club.model}`),
          detailedSpecs: {
            technologyHighlights: topWedge.club.keyTechnologies.slice(0, 3)
          }
        }]
      };
      recommendations.push(wedgeRec);
    }
    
    return recommendations;
  }

  async getAIBallRecommendations(request: {
    userProfile: UserProfile;
    swingSpeed: number;
    preferredFeel: string;
    primaryGoal: string;
    budget: string;
  }): Promise<any[]> {
    const systemMessage = `You are a Master Golf Ball Fitter with deep knowledge of golf ball technology and how different balls perform for different golfers.

Your expertise includes:
- Understanding compression, construction, and dimple patterns
- Knowledge of current golf balls from all major manufacturers
- Ability to match ball characteristics to swing speed and playing style
- Experience fitting golfers of all skill levels`;

    const prompt = `Recommend golf balls for this golfer:

GOLFER PROFILE:
- Swing Speed: ${request.swingSpeed} mph
- Ball Flight: ${request.userProfile.ballFlight}
- Skill Level: ${request.userProfile.skillLevel}
- Playing Experience: ${request.userProfile.playingExperience}
- Preferred Feel: ${request.preferredFeel}
- Primary Goal: ${request.primaryGoal}
- Budget: ${request.budget}

Provide 3 ball recommendations in JSON format:
[
  {
    "brand": "Brand Name",
    "model": "Model Name", 
    "type": "Ball Category",
    "compression": 85,
    "dimples": 352,
    "construction": "3-piece",
    "features": ["Feature 1", "Feature 2", "Feature 3"],
    "matchScore": 95,
    "reasons": ["Reason 1", "Reason 2", "Reason 3"],
    "price": "$50/dozen"
  }
]

Focus on balls that match their swing speed, playing style, and goals. Explain why each ball is recommended.`;

    try {
      const response = await this.makeAIRequest(prompt, systemMessage);
      const jsonMatch = response.match(/\[[\s\S]*\]/);
      if (jsonMatch) {
        return JSON.parse(jsonMatch[0]);
      }
      throw new Error("Invalid AI response format");
    } catch (error) {
      console.error("AI Ball Recommendations Error:", error);
      throw error;
    }
  }

  async getAIGeneralAdvice(
    question: string,
    userProfile?: UserProfile,
  ): Promise<string> {
    const systemMessage = `You are a knowledgeable golf professional with expertise in all aspects of the game. You provide helpful, accurate advice to golfers of all skill levels.

Keep responses conversational but informative, and tailor advice to the golfer's skill level when profile information is available.`;

    const profileContext = userProfile
      ? `\nGolfer context: ${userProfile.handicap ? userProfile.handicap + " handicap" : "Handicap not specified"}, ${userProfile.skillLevel} level, typical ${userProfile.ballFlight} ball flight.`
      : "";

    const prompt = `${question}${profileContext}`;

    try {
      return await this.makeAIRequest(prompt, systemMessage);
    } catch (error) {
      console.error("AI General Advice Error:", error);
      return "I wasn't able to generate advice right now. Please try again in a moment.";
    }
  }

  async getAIStarterSetRecommendations(request: {
    frequency: string;
    courseType: string;
    missPattern: string;
    confidenceClub: string;
    budget: string;
    skillLevel: string;
    tier: string;
  }): Promise<string> {
    const systemMessage = `You are a Master Club Fitter and equipment expert specializing in helping beginners and intermediate golfers find the perfect starter set. You have deep knowledge of all major golf equipment brands, their technologies, and how different equipment characteristics benefit different player types.`;

    const prompt = `Generate a personalized explanation for why a "${request.tier}" tier starter set is perfect for this golfer:

GOLFER PROFILE:
- Playing Frequency: ${request.frequency}
- Course Type: ${request.courseType}
- Miss Pattern: ${request.missPattern}
- Most Confident Club: ${request.confidenceClub}
- Budget: ${request.budget}
- Skill Level: ${request.skillLevel}

RECOMMENDATION TIER: ${request.tier === 'good' ? 'Essential' : request.tier === 'better' ? 'Progressive Improvement' : 'Premium Starter Collection'}

Provide a 2-3 sentence explanation that:
1. Addresses their specific needs based on playing frequency and miss pattern
2. Explains why this price tier makes sense for their commitment level
3. Mentions specific benefits they'll get from this tier

Be conversational, encouraging, and specific. Don't use JSON format, just provide the explanation text.`;

    try {
      return await this.makeAIRequest(prompt, systemMessage);
    } catch (error) {
      console.error("AI Starter Set Recommendation Error:", error);
      return "This tier is a great match for your playing style and budget. It includes quality clubs that will help you improve as you develop your game.";
    }
  }

  async getDailyGolfTip(request: {
    userProfile: UserProfile;
    weather?: WeatherConditions;
  }): Promise<string> {
    const systemMessage = `You are a PGA Tour pro and master instructor. You provide one actionable, personalized golf tip per day that helps golfers improve immediately. Your tips are specific, practical, and tailored to the golfer's skill level and current challenges.`;

    const weatherContext = request.weather?.available
      ? `\nWeather Today: ${request.weather.temperature}°F, wind ${request.weather.windSpeed}mph ${request.weather.windDirection}`
      : request.weather
        ? '\nWeather Today: live weather unavailable — do not invent a location or conditions'
        : '';

    const prompt = `Generate ONE personalized daily golf tip for this golfer:

PROFILE:
- Handicap: ${request.userProfile.handicap || 18}
- Skill Level: ${request.userProfile.skillLevel}
- Ball Flight: ${request.userProfile.ballFlight}
- Main Issues: ${request.userProfile.currentIssues.join(", ") || "General improvement"}
- Goals: ${request.userProfile.golfGoals.join(", ") || "Lower scores"}${weatherContext}

Provide ONE specific, actionable tip (2-3 sentences max) that:
1. Addresses their current issues or goals
2. Is something they can apply TODAY
3. Is appropriate for their skill level
4. Considers weather if playing today

Example format: "Quick Tip: With today's 15mph winds, club up 1-2 clubs and aim 10 yards left of target to compensate for crosswind."

IMPORTANT: Do NOT use markdown formatting (no **, *, #, or other markup). Write in plain text only. Do not start with an emoji.
Be encouraging and specific. No generic advice.`;

    try {
      return await this.makeAIRequest(prompt, systemMessage);
    } catch (error) {
      console.error("AI Daily Tip Error:", error);
      return "Focus on tempo today — a smooth, rhythmic swing produces more consistent shots than swinging hard. Try counting '1-2' on your backswing and '3' at impact.";
    }
  }

  async answerGolfQuestion(request: {
    question: string;
    userProfile?: UserProfile;
    context?: string;
  }): Promise<string> {
    const systemMessage = `You are a PGA-certified golf instructor and equipment expert. You answer golf questions clearly, accurately, and helpfully. When possible, tailor answers to the golfer's skill level and profile.`;

    const profileContext = request.userProfile
      ? `\n\nGolfer Context: ${request.userProfile.handicap ? request.userProfile.handicap + " handicap" : "Beginner-Intermediate"}, ${request.userProfile.skillLevel} level, plays ${request.userProfile.courseFrequency}.`
      : '';

    const articleContext = request.context
      ? `\n\nRelated Article Context: ${request.context}`
      : '';

    const prompt = `Question: ${request.question}${profileContext}${articleContext}

Provide a clear, helpful answer that:
1. Directly answers the question
2. Tailors advice to their skill level when known
3. Includes specific examples or numbers when relevant
4. Suggests next steps if appropriate

Keep it conversational and practical.`;

    try {
      return await this.makeAIRequest(prompt, systemMessage);
    } catch (error) {
      console.error("AI Answer Question Error:", error);
      return "I wasn't able to answer that right now. Please try again in a moment.";
    }
  }

  /**
   * CONTEXT-AWARE gap analysis - determines club type based on ADJACENT clubs, not just distance
   * This is critical: a gap between Driver and 3-Wood needs a fairway wood, NOT another driver
   */
  private getClubTypeForGap(
    affectedRange?: { from: number; to: number }, 
    problemId?: string,
    affectedClubs?: Array<{ type: string }>
  ): {
    type: 'driver' | 'fairway' | 'hybrid' | 'iron' | 'wedge';
    clubName: string;
    examples: { value: { brand: string; model: string }; mid: { brand: string; model: string }; premium: { brand: string; model: string } };
    adjustmentFirst?: string; // Suggest adjustment before new club
  } {
    // USER-SPECIFIED club models by tier - STRUCTURED format to prevent AI duplication issues
    const CLUB_EXAMPLES = {
      driver: {
        value: { brand: 'Cobra', model: 'AeroJet' },
        mid: { brand: 'Callaway', model: 'Paradym X' },
        premium: { brand: 'Titleist', model: 'TSR2' }
      },
      fairway: {
        value: { brand: 'Tour Edge', model: 'Exotics C723' },
        mid: { brand: 'Ping', model: 'G430' },
        premium: { brand: 'Titleist', model: 'TSR3' }
      },
      hybrid: {
        value: { brand: 'Cobra', model: 'Air-X Hybrid' },
        mid: { brand: 'Ping', model: 'G430 Hybrid' },
        premium: { brand: 'Titleist', model: 'TSR Hybrid' }
      },
      iron: {
        value: { brand: 'Takomo', model: '101T' },
        mid: { brand: 'Mizuno', model: 'JPX 923 Hot Metal' },
        premium: { brand: 'Titleist', model: 'T200' }
      },
      wedge: {
        value: { brand: 'Cleveland', model: 'CBX 4' },
        mid: { brand: 'Callaway', model: 'Jaws MD5' },
        premium: { brand: 'Titleist', model: 'Vokey SM10' }
      }
    };

    // Parse adjacent clubs from problem ID (format: "gap-driver-3-wood" or "gap-3-wood-3-hybrid")
    const parseClubsFromId = (id: string): { upper: string; lower: string } | null => {
      if (!id?.startsWith('gap-')) return null;
      const parts = id.replace('gap-', '').split('-');
      // Reconstruct club names (e.g., "3", "wood" -> "3-wood")
      if (parts.length >= 2) {
        // Handle cases like "driver-3-wood", "3-wood-3-hybrid", "pw-gw"
        if (parts[0] === 'driver') {
          return { upper: 'driver', lower: parts.slice(1).join('-') };
        }
        // Try to find the split point between two clubs
        for (let i = 1; i < parts.length; i++) {
          const upper = parts.slice(0, i).join('-');
          const lower = parts.slice(i).join('-');
          if (upper && lower) return { upper, lower };
        }
      }
      return null;
    };

    const adjacentClubs = parseClubsFromId(problemId || '');
    
    // CONTEXT-AWARE LOGIC: What's between these two clubs?
    if (adjacentClubs) {
      const { upper, lower } = adjacentClubs;
      
      // Driver to Wood gap: NEVER recommend another driver - suggest fairway wood
      if (upper === 'driver' && (lower.includes('wood') || lower.includes('hybrid'))) {
        return {
          type: 'fairway',
          clubName: '5 Wood or Mini-Driver',
          examples: CLUB_EXAMPLES.fairway,
          adjustmentFirst: 'Try adjusting your 3-wood loft down, or consider a strong 3-wood (13.5°) before adding a club'
        };
      }
      
      // Wood to Wood gap (e.g., 3-wood to 5-wood): suggest 4-wood or 7-wood
      if (upper.includes('wood') && lower.includes('wood')) {
        return {
          type: 'fairway',
          clubName: '4 Wood or 7 Wood',
          examples: CLUB_EXAMPLES.fairway,
          adjustmentFirst: 'Check loft gaps on existing woods - consider bending or different shaft'
        };
      }
      
      // Wood to Hybrid gap: suggest higher-loft wood or lower hybrid
      if (upper.includes('wood') && lower.includes('hybrid')) {
        return {
          type: 'fairway',
          clubName: '7 Wood or 2 Hybrid',
          examples: CLUB_EXAMPLES.fairway,
          adjustmentFirst: 'A 7-wood often fills this gap better than adding another hybrid'
        };
      }
      
      // Hybrid to Hybrid gap: suggest intermediate hybrid
      if (upper.includes('hybrid') && lower.includes('hybrid')) {
        return {
          type: 'hybrid',
          clubName: 'Hybrid (intermediate loft)',
          examples: CLUB_EXAMPLES.hybrid,
          adjustmentFirst: 'Consider swapping one hybrid for a different loft rather than adding'
        };
      }
      
      // Hybrid to Iron gap: ONLY suggest utility iron or driving iron
      // CRITICAL: DO NOT suggest a lower-numbered hybrid (e.g., 4H for 5H-to-5I gap)
      // A 4-Hybrid sits ABOVE the 5-Hybrid, not between 5H and 5I
      if (upper.includes('hybrid') && lower.includes('iron')) {
        return {
          type: 'iron',
          clubName: 'Utility Iron or Driving Iron',
          examples: {
            value: { brand: 'Titleist', model: 'U500' },
            mid: { brand: 'TaylorMade', model: 'P-DHY' },
            premium: { brand: 'Titleist', model: 'U505' }
          },
          adjustmentFirst: 'A utility iron or driving iron is the ONLY correct solution for hybrid-to-iron transition gaps. These clubs provide iron-like flight with hybrid forgiveness.'
        };
      }
      
      // Iron to Iron gap: suggest intermediate iron
      if (upper.includes('iron') && lower.includes('iron')) {
        return {
          type: 'iron',
          clubName: 'Iron',
          examples: CLUB_EXAMPLES.iron,
          adjustmentFirst: 'Your iron set may need re-gapping - consider a fitting to check lofts'
        };
      }
      
      // Wedge gaps (PW, GW, AW, SW, LW)
      const wedgeTypes = ['pw', 'gw', 'aw', 'sw', 'lw'];
      if (wedgeTypes.includes(upper) || wedgeTypes.includes(lower) || 
          upper.includes('wedge') || lower.includes('wedge')) {
        const midpoint = affectedRange ? (affectedRange.from + affectedRange.to) / 2 : 90;
        const loft = midpoint <= 80 ? '56°-58°' : midpoint <= 100 ? '52°-54°' : '48°-50°';
        return {
          type: 'wedge',
          clubName: `${loft} Wedge`,
          examples: CLUB_EXAMPLES.wedge,
          adjustmentFirst: 'Check your current wedge lofts - many sets have inconsistent gaps'
        };
      }
    }
    
    // FALLBACK: Use distance-based logic if we couldn't parse clubs
    const midpoint = affectedRange ? (affectedRange.from + affectedRange.to) / 2 : 175;
    
    if (midpoint <= 100) {
      return {
        type: 'wedge',
        clubName: `${midpoint <= 80 ? '56°-58°' : '50°-52°'} Wedge`,
        examples: CLUB_EXAMPLES.wedge,
        adjustmentFirst: 'Check your wedge loft spacing first'
      };
    } else if (midpoint <= 160) {
      return {
        type: 'iron',
        clubName: midpoint <= 130 ? '8-9 Iron' : '6-7 Iron',
        examples: CLUB_EXAMPLES.iron,
        adjustmentFirst: 'Consider hybrid replacements for long irons'
      };
    } else if (midpoint <= 210) {
      return {
        type: 'hybrid',
        clubName: midpoint <= 180 ? '5-6 Hybrid' : '3-4 Hybrid',
        examples: CLUB_EXAMPLES.hybrid
      };
    } else if (midpoint <= 260) {
      return {
        type: 'fairway',
        clubName: midpoint <= 230 ? '5-7 Wood' : '3 Wood',
        examples: CLUB_EXAMPLES.fairway,
        adjustmentFirst: 'Try a strong 3-wood or mini-driver before adding another club'
      };
    } else {
      // Even for 260+ yards, if there's already a driver, suggest fairway wood
      return {
        type: 'fairway',
        clubName: '3 Wood or Mini-Driver',
        examples: CLUB_EXAMPLES.fairway,
        adjustmentFirst: 'A strong 3-wood or mini-driver can help fill this long-distance gap'
      };
    }
  }

  /**
   * Get AI-powered solutions for a specific bag problem
   * Returns budget-tiered club recommendations with category-specific pricing
   * NOW INCLUDES: adjustment suggestions before new club purchases
   * NOW USES: Current club distances from golfBag for accurate recommendations
   * NOW USES: Gap rules from bagAnalyzer for accurate club eligibility
   */
  async getAISolutionsForProblem(
    problem: { 
      id: string;
      type: string;
      title: string;
      description: string;
      solutionHint: string;
      affectedRange?: { from: number; to: number };
      affectedClubs?: Array<{ type: string; distance?: number }>;
      gapRules?: {
        specificSuggestion: string;
        validSolutions: string[];
        forbidden: string[];
        adjustmentFirst: string;
      };
    },
    userProfile: UserProfile,
    budget: string,
    clubDistances?: { club: string; distance: number }[]
  ): Promise<Array<{
    tier: 'value' | 'mid' | 'premium' | 'adjustment';
    tierLabel: string;
    priceRange: string;
    brand: string;
    model: string;
    clubName: string;
    clubType: string;
    intendedCarry: string;
    reasoning: string;
  }>> {
    // CATEGORY-SPECIFIC PRICE RANGES (user-specified exact tiers)
    const priceRanges = {
      driver: { value: '$100-200', mid: '$200-300', premium: '$300+' },
      fairway: { value: '$100-180', mid: '$180-250', premium: '$250+' },
      hybrid: { value: '$100-180', mid: '$180-250', premium: '$250+' },
      iron: { value: '$500-700 (set)', mid: '$700-1100 (set)', premium: '$1100+ (set)' },
      wedge: { value: '$50-75', mid: '$75-105', premium: '$105+' }
    };

    // CONTEXT-AWARE CLUB TYPE DETECTION based on ADJACENT clubs
    // PRIORITY: Use problem.gapRules if available (from bagAnalyzer), otherwise fall back to getClubTypeForGap
    const clubInfo = this.getClubTypeForGap(problem.affectedRange, problem.id, problem.affectedClubs);
    
    // Override with gap rules from bagAnalyzer if available (more accurate)
    const gapRules = problem.gapRules;
    const specificClubName = gapRules?.specificSuggestion || clubInfo.clubName;
    const adjustmentSuggestion = gapRules?.adjustmentFirst || clubInfo.adjustmentFirst;
    const forbiddenTypes = gapRules?.forbidden || [];
    const validSolutions = gapRules?.validSolutions || [];
    
    const prices = priceRanges[clubInfo.type];

    // Build solutions array - start with adjustment suggestion if available
    const solutions: Array<{
      tier: 'value' | 'mid' | 'premium' | 'adjustment';
      tierLabel: string;
      priceRange: string;
      brand: string;
      model: string;
      clubName: string;
      clubType: string;
      intendedCarry: string;
      reasoning: string;
    }> = [];

    // Calculate intended carry range from affected range
    const intendedCarryRange = problem.affectedRange 
      ? `${problem.affectedRange.from + 5}-${problem.affectedRange.to - 5}`
      : 'N/A';

    // ADD ADJUSTMENT SUGGESTION FIRST (if available)
    if (adjustmentSuggestion) {
      solutions.push({
        tier: 'adjustment' as const,
        tierLabel: 'Try First',
        priceRange: '$0-50 (fitting/adjustment)',
        brand: 'Adjustment',
        model: 'Loft/Shaft Tweak',
        clubName: 'Existing Club Adjustment',
        clubType: 'adjustment',
        intendedCarry: intendedCarryRange,
        reasoning: adjustmentSuggestion
      });
    }
    
    // Build club distances section for AI context
    const distancesSection = clubDistances && clubDistances.length > 0
      ? `\nTHIS GOLFER'S ACTUAL CLUB DISTANCES (sorted by distance):
${clubDistances
  .sort((a, b) => (b.distance || 0) - (a.distance || 0))
  .map(cd => `- ${cd.club}: ${cd.distance} yards`)
  .join('\n')}`
      : '';

    // Build forbidden and valid solutions sections for AI
    const forbiddenSection = forbiddenTypes.length > 0 
      ? `\n⛔ FORBIDDEN CLUB TYPES (NEVER recommend these): ${forbiddenTypes.join(', ')}`
      : '';
    const validSection = validSolutions.length > 0
      ? `\n✅ VALID SOLUTIONS ONLY: ${validSolutions.join(', ')}`
      : '';

    const systemMessage = `You are a Master Club Fitter helping solve a specific problem in a golfer's bag. You provide precise, budget-appropriate club recommendations with SPECIFIC club types, numbers, and intended carry ranges.

CRITICAL RULES:
1. You MUST recommend ONLY ${clubInfo.type.toUpperCase()}S for this gap
2. NEVER recommend a driver for gaps between driver and fairway wood
3. For top-of-bag gaps, suggest fairway woods or mini-drivers
4. Use the EXACT models provided in the examples
5. EVERY recommendation MUST include the specific club number (e.g., "4 Hybrid" not just "Hybrid")
6. EVERY recommendation MUST specify the intended carry yardage range
7. Reference the golfer's ACTUAL club distances when explaining the gap
8. STRICTLY FOLLOW the forbidden/valid solutions list - this is non-negotiable

HYBRID-TO-IRON TRANSITION GAP RULES (CRITICAL):
- For gaps between a hybrid and an iron (e.g., 5-Hybrid to 5-Iron), NEVER recommend a lower-numbered hybrid
- Example: For a gap between 5-Hybrid (230yds) and 5-Iron (205yds), DO NOT suggest 4-Hybrid
- A 4-Hybrid sits ABOVE the 5-Hybrid in the bag (longer distance), NOT between 5H and 5I
- The ONLY valid solutions for hybrid-to-iron gaps are: Utility Iron, Driving Iron, or loft adjustments
- Utility irons provide iron-like ball flight with hybrid-level forgiveness - perfect for this transition zone`;

    const prompt = `PROBLEM TO SOLVE:
${problem.title}
${problem.description}

TARGET DISTANCE RANGE: ${problem.affectedRange ? `${problem.affectedRange.from}-${problem.affectedRange.to} yards` : 'Not specified'}
REQUIRED CLUB TYPE: ${clubInfo.type.toUpperCase()} (specifically: ${specificClubName})${forbiddenSection}${validSection}
${distancesSection}

GOLFER PROFILE:
- Handicap: ${userProfile.handicap || 18}
- Swing Speed: ${userProfile.swingSpeed || 'Unknown'} mph
- Ball Flight: ${userProfile.ballFlight || 'straight'}
- Skill Level: ${userProfile.skillLevel}
- Preferred Budget: ${budget === 'budget' ? 'Value' : budget === 'premium' ? 'Premium' : 'Mid-Range'}

RECOMMENDED CLUBS BY TIER (USE THESE EXACT BRAND + MODEL COMBINATIONS):
- Value: Brand="${clubInfo.examples.value.brand}" Model="${clubInfo.examples.value.model}"
- Mid-Range: Brand="${clubInfo.examples.mid.brand}" Model="${clubInfo.examples.mid.model}"
- Premium: Brand="${clubInfo.examples.premium.brand}" Model="${clubInfo.examples.premium.model}"

Provide exactly 3 ${clubInfo.type} recommendations - one for each budget tier.
Use the EXACT brand and model names from above - DO NOT modify or repeat parts of the name.

PRICING (must follow exactly):
- Value: ${prices.value}
- Mid-Range: ${prices.mid}
- Premium: ${prices.premium}

⚠️ CRITICAL OUTPUT REQUIREMENTS:
1. "brand" and "model" MUST match exactly what's listed above - no modifications!
2. "clubType" MUST be specific (e.g., "4 Hybrid", "5-Wood", "52° Wedge") - use "${specificClubName}" as guide
3. "intendedCarry" MUST be a yardage range (e.g., "195-205")
4. "reasoning" MUST reference the golfer's actual club distances and explain WHY this fills the gap
5. ONLY suggest clubs from the VALID SOLUTIONS list if provided

JSON FORMAT:
[
  {
    "tier": "value",
    "tierLabel": "Value",
    "priceRange": "${prices.value}",
    "brand": "${clubInfo.examples.value.brand}",
    "model": "${clubInfo.examples.value.model}",
    "clubName": "${specificClubName}",
    "clubType": "${specificClubName}",
    "intendedCarry": "${intendedCarryRange}",
    "reasoning": "With your [upper club] at [X yards] and [lower club] at [Y yards], this ${specificClubName} tuned to ${intendedCarryRange} yards bridges the gap..."
  },
  {
    "tier": "mid",
    "tierLabel": "Mid-Range",
    "priceRange": "${prices.mid}",
    "brand": "${clubInfo.examples.mid.brand}",
    "model": "${clubInfo.examples.mid.model}",
    "clubName": "${specificClubName}",
    "clubType": "${specificClubName}",
    "intendedCarry": "${intendedCarryRange}",
    "reasoning": "Why this solves the gap with specific distance reference"
  },
  {
    "tier": "premium",
    "tierLabel": "Premium",
    "priceRange": "${prices.premium}",
    "brand": "${clubInfo.examples.premium.brand}",
    "model": "${clubInfo.examples.premium.model}",
    "clubName": "${specificClubName}",
    "clubType": "${specificClubName}",
    "intendedCarry": "${intendedCarryRange}",
    "reasoning": "Why this solves the gap with specific distance reference"
  }
]`;

    try {
      const response = await this.makeAIRequest(prompt, systemMessage);
      const jsonMatch = response.match(/\[[\s\S]*\]/);
      if (jsonMatch) {
        const aiSolutions = JSON.parse(jsonMatch[0]);
        
        // HARD FILTER: Reject solutions that contain forbidden club types
        // This is the enforcement layer - AI can suggest whatever it wants, we filter it out
        const isHybridToIronGap = problem.id?.includes('hybrid') && problem.id?.includes('iron');
        
        const filteredSolutions = aiSolutions.map((sol: any) => {
          const clubTypeStr = (sol.clubType || '').toLowerCase();
          const clubNameStr = (sol.clubName || '').toLowerCase();
          
          // For hybrid-to-iron gaps: REJECT any hybrid recommendation
          // A hybrid CANNOT fill a gap between a hybrid and an iron - it sits above the hybrid
          if (isHybridToIronGap && (clubTypeStr.includes('hybrid') || clubNameStr.includes('hybrid'))) {
            // REPLACE with correct recommendation: Utility Iron
            return {
              ...sol,
              clubType: 'Utility Iron',
              clubName: 'Utility Iron or Driving Iron',
              reasoning: `For the gap between your hybrid and iron, a utility iron provides iron-like ball flight with hybrid forgiveness - the only correct solution for this transition zone. ${sol.reasoning || ''}`
            };
          }
          
          // Check against forbidden list (for other gap types)
          const isForbidden = forbiddenTypes.some((forbidden: string) => 
            clubTypeStr.includes(forbidden.toLowerCase()) || clubNameStr.includes(forbidden.toLowerCase())
          );
          
          if (isForbidden) {
            return {
              ...sol,
              clubType: specificClubName,
              clubName: specificClubName
            };
          }
          
          return sol;
        });
        
        // VALIDATION: Ensure all solutions have required specificity fields
        const validatedSolutions = filteredSolutions.map((sol: any) => {
          // Check if clubType is specific enough (has a number or degree)
          const hasSpecificClubType = sol.clubType && 
            (sol.clubType.match(/\d/) || sol.clubType.includes('°') || sol.clubType.toLowerCase().includes('utility'));
          
          // Check if intendedCarry is a valid range
          const hasValidCarry = sol.intendedCarry && 
            sol.intendedCarry.match(/\d+-\d+/);
          
          // Check if reasoning references distances
          const hasDistanceReference = sol.reasoning && 
            (sol.reasoning.match(/\d+\s*yard/) || sol.reasoning.includes('carries'));
          
          // If missing specificity, enhance with defaults
          return {
            ...sol,
            clubType: hasSpecificClubType ? sol.clubType : specificClubName,
            intendedCarry: hasValidCarry ? sol.intendedCarry : intendedCarryRange,
            // Add warning if reasoning is too generic
            reasoning: !hasDistanceReference && sol.reasoning
              ? `${sol.reasoning} (Target: ${intendedCarryRange} yards)`
              : sol.reasoning
          };
        });
        
        // Sort AI solutions to put user's preferred tier first
        const tierOrder = budget === 'budget' ? ['value', 'mid', 'premium'] :
                          budget === 'premium' ? ['premium', 'mid', 'value'] :
                          ['mid', 'value', 'premium'];
        
        validatedSolutions.sort((a: any, b: any) => 
          tierOrder.indexOf(a.tier) - tierOrder.indexOf(b.tier)
        );
        
        // Combine: adjustment first (if exists), then validated AI solutions
        const finalSolutions = [...solutions, ...validatedSolutions];
        return finalSolutions;
      }
      throw new Error("Invalid AI response format");
    } catch (error) {
      console.error("AI Solutions Error:", error);
      // Return adjustment + fallback solutions
      const fallback = this.getFallbackSolutions(problem, clubInfo, prices, budget);
      return [...solutions, ...fallback];
    }
  }

  /**
   * Fallback solutions when AI fails - uses USER-SPECIFIED club models
   */
  private getFallbackSolutions(
    problem: any,
    clubInfo: { type: string; clubName: string; examples: { value: { brand: string; model: string }; mid: { brand: string; model: string }; premium: { brand: string; model: string } } },
    prices: { value: string; mid: string; premium: string },
    budget: string
  ): Array<{
    tier: 'value' | 'mid' | 'premium';
    tierLabel: string;
    priceRange: string;
    brand: string;
    model: string;
    clubName: string;
    clubType: string;
    intendedCarry: string;
    reasoning: string;
  }> {
    const targetDistance = problem.affectedRange 
      ? Math.round((problem.affectedRange.from + problem.affectedRange.to) / 2)
      : 175;
    
    const intendedCarryRange = problem.affectedRange 
      ? `${problem.affectedRange.from + 5}-${problem.affectedRange.to - 5}`
      : `${targetDistance - 10}-${targetDistance + 10}`;

    // Check if this is a hybrid-to-iron gap - if so, FORCE utility iron recommendations
    const isHybridToIronGap = problem.id?.includes('hybrid') && problem.id?.includes('iron');
    
    // USER-SPECIFIED club models by category and tier
    const fallbackByType: Record<string, { value: { brand: string; model: string }; mid: { brand: string; model: string }; premium: { brand: string; model: string } }> = {
      wedge: {
        value: { brand: 'Kirkland', model: 'Signature Wedge' },
        mid: { brand: 'Callaway', model: 'Jaws Full Toe' },
        premium: { brand: 'Titleist', model: 'Vokey SM10' }
      },
      iron: {
        value: { brand: 'Takomo', model: '101T' },
        mid: { brand: 'Mizuno', model: 'JPX 923 Hot Metal' },
        premium: { brand: 'Titleist', model: 'T200' }
      },
      // Utility irons for hybrid-to-iron transition gaps
      'utility-iron': {
        value: { brand: 'Titleist', model: 'U500' },
        mid: { brand: 'TaylorMade', model: 'P-DHY' },
        premium: { brand: 'Titleist', model: 'U505' }
      },
      hybrid: {
        value: { brand: 'Cobra', model: 'Air-X Hybrid' },
        mid: { brand: 'Ping', model: 'G430 Hybrid' },
        premium: { brand: 'Titleist', model: 'TSR Hybrid' }
      },
      fairway: {
        value: { brand: 'Tour Edge', model: 'Exotics C723' },
        mid: { brand: 'Ping', model: 'G430 Fairway' },
        premium: { brand: 'Titleist', model: 'TSR Fairway' }
      },
      driver: {
        value: { brand: 'Cobra', model: 'AeroJet' },
        mid: { brand: 'Callaway', model: 'Paradym X' },
        premium: { brand: 'Titleist', model: 'TSR2' }
      }
    };

    // For hybrid-to-iron gaps: ALWAYS use utility-iron, never hybrid
    const effectiveType = isHybridToIronGap ? 'utility-iron' : clubInfo.type;
    const effectiveClubName = isHybridToIronGap ? 'Utility Iron' : clubInfo.clubName;
    const typeOptions = fallbackByType[effectiveType] || fallbackByType.iron;

    const reasoningPrefix = isHybridToIronGap 
      ? 'For hybrid-to-iron transition gaps, a utility iron provides iron-like ball flight with hybrid forgiveness - '
      : '';
    
    const solutions = [
      {
        tier: 'value' as const,
        tierLabel: 'Value',
        priceRange: prices.value,
        brand: typeOptions.value.brand,
        model: typeOptions.value.model,
        clubName: effectiveClubName,
        clubType: effectiveClubName,
        intendedCarry: intendedCarryRange,
        reasoning: `${reasoningPrefix}Forgiving ${effectiveType} tuned to ${intendedCarryRange} yards for consistent shots with easy launch`
      },
      {
        tier: 'mid' as const,
        tierLabel: 'Mid-Range',
        priceRange: prices.mid,
        brand: typeOptions.mid.brand,
        model: typeOptions.mid.model,
        clubName: effectiveClubName,
        clubType: effectiveClubName,
        intendedCarry: intendedCarryRange,
        reasoning: `${reasoningPrefix}Modern technology for precise ${intendedCarryRange} yard distance control`
      },
      {
        tier: 'premium' as const,
        tierLabel: 'Premium',
        priceRange: prices.premium,
        brand: typeOptions.premium.brand,
        model: typeOptions.premium.model,
        clubName: effectiveClubName,
        clubType: effectiveClubName,
        intendedCarry: intendedCarryRange,
        reasoning: `${reasoningPrefix}Tour-level performance with adjustability for precise ${intendedCarryRange} yard gapping`
      }
    ];

    // Sort by user preference
    const tierOrder = budget === 'budget' ? ['value', 'mid', 'premium'] :
                      budget === 'premium' ? ['premium', 'mid', 'value'] :
                      ['mid', 'value', 'premium'];
    
    solutions.sort((a, b) => tierOrder.indexOf(a.tier) - tierOrder.indexOf(b.tier));
    
    return solutions;
  }

  /**
   * AI-Powered Bag Analysis
   * Takes local analysis as input and enhances with AI-driven insights
   */
  async getAIBagAnalysis(
    userProfile: UserProfile,
    localProblems: Array<{
      id: string;
      type: string;
      severity: string;
      title: string;
      description: string;
      impact: string;
      affectedRange?: { from: number; to: number };
    }>
  ): Promise<{
    enhancedProblems: Array<{
      id: string;
      type: string;
      severity: string;
      title: string;
      description: string;
      impact: string;
      affectedRange?: { from: number; to: number };
      aiPriority?: number;
      aiReason?: string;
    }>;
    aiInsights: string[];
    priorityRecommendation: string;
    scoringImpact: string;
  }> {
    const bag = userProfile.golfBag || [];
    const handicap = userProfile.handicap || 18;
    const skillLevel = userProfile.skillLevel || 'intermediate';
    const ballFlight = userProfile.ballFlight || 'straight';
    const swingSpeed = userProfile.swingSpeed;
    const currentIssues = userProfile.currentIssues || [];

    // Build bag summary for AI
    const bagSummary = bag.map(club => 
      `${club.type}: ${club.brand || 'Unknown'} ${club.model || ''} - ${club.distance || 'no distance'}yds`
    ).join('\n');

    const problemsSummary = localProblems.map(p => 
      `- [${p.severity.toUpperCase()}] ${p.title}: ${p.description} (${p.impact})`
    ).join('\n');

    const systemMessage = `You are ClubIQ's AI Golf Bag Analyst - a Master Club Fitter with 20+ years experience. 
You analyze golf bags to identify the MOST IMPACTFUL improvements for each golfer's specific situation.
Your insights are personalized, actionable, and focus on what will actually help THIS golfer score better.`;

    const prompt = `Analyze this golfer's bag and provide personalized insights:

GOLFER PROFILE:
- Handicap: ${handicap}
- Skill Level: ${skillLevel}
- Swing Speed: ${swingSpeed ? `${swingSpeed} mph` : 'Unknown'}
- Ball Flight: ${ballFlight}
- Current Issues: ${currentIssues.length > 0 ? currentIssues.join(', ') : 'None reported'}

CURRENT BAG:
${bagSummary || 'No clubs in bag'}

DETECTED PROBLEMS (from local analysis):
${problemsSummary || 'No problems detected'}

Based on this golfer's profile and bag, provide:

1. PRIORITIZED PROBLEMS: Re-order the detected problems by what will have the BIGGEST impact for THIS specific golfer. A ${handicap} handicap ${skillLevel} player has different priorities than a scratch golfer.

2. AI INSIGHTS: 3-4 personalized insights that go beyond the basic gap detection. Consider:
   - How their ball flight (${ballFlight}) affects club selection
   - Whether their bag matches their skill level
   - Specific opportunities they might be missing
   - Any red flags or quick wins

3. PRIORITY RECOMMENDATION: The ONE thing they should fix first and why

4. SCORING IMPACT: Estimated strokes they could save per round by addressing issues

Respond in JSON:
{
  "problemPriorities": [
    { "id": "problem-id", "priority": 1, "reason": "Why this is high priority for this golfer" }
  ],
  "aiInsights": [
    "Personalized insight 1",
    "Personalized insight 2",
    "Personalized insight 3"
  ],
  "priorityRecommendation": "The single most important change for this golfer",
  "scoringImpact": "Could save X-Y strokes per round by..."
}`;

    try {
      const response = await this.makeAIRequest(prompt, systemMessage);
      const jsonMatch = response.match(/\{[\s\S]*\}/);
      
      if (jsonMatch) {
        const aiResult = JSON.parse(jsonMatch[0]);
        
        // Enhance problems with AI priorities
        const enhancedProblems = localProblems.map(problem => {
          const aiPriority = aiResult.problemPriorities?.find(
            (p: { id: string }) => p.id === problem.id
          );
          return {
            ...problem,
            aiPriority: aiPriority?.priority,
            aiReason: aiPriority?.reason
          };
        });

        // Sort by AI priority if available
        enhancedProblems.sort((a, b) => {
          if (a.aiPriority && b.aiPriority) {
            return a.aiPriority - b.aiPriority;
          }
          return 0;
        });

        return {
          enhancedProblems,
          aiInsights: aiResult.aiInsights || [],
          priorityRecommendation: aiResult.priorityRecommendation || 'Complete your bag profile for personalized recommendations',
          scoringImpact: aiResult.scoringImpact || 'Add more club data to estimate scoring impact'
        };
      }
      
      throw new Error("Invalid AI response format");
    } catch (error) {
      console.error("AI Bag Analysis Error:", error);
      // Return local problems unchanged with generic insights
      return {
        enhancedProblems: localProblems.map(p => ({ ...p })),
        aiInsights: [
          `Your ${handicap} handicap suggests focusing on consistency over distance`,
          bag.length < 10 ? 'Consider filling out your bag for complete coverage' : 'Good club variety in your bag',
          ballFlight !== 'straight' ? `Your ${ballFlight} tendency may benefit from game-improvement clubs` : 'Your straight ball flight gives you flexibility in club selection'
        ],
        priorityRecommendation: localProblems.length > 0 
          ? `Address "${localProblems[0].title}" first for the biggest impact`
          : 'Your bag looks well-optimized! Keep tracking your distances.',
        scoringImpact: localProblems.length > 0 
          ? `Fixing ${localProblems.length} issue${localProblems.length > 1 ? 's' : ''} could save 1-3 strokes per round`
          : 'No major issues detected - focus on practice and course management'
      };
    }
  }

  /**
   * General chat response for Ask ClubIQ
   */
  async getChatResponse(
    userMessage: string,
    systemContext: string,
    conversationHistory: Array<{ role: 'user' | 'assistant'; content: string }>
  ): Promise<string> {
    try {
      if (userTextRequestsCrisisHelp(userMessage)) {
        return CRISIS_HELP_REPLY;
      }
      // Build the full message array including conversation history
      // This ensures the AI has context from previous messages in the chat
      const messages = [
        {
          role: 'system' as const,
          content: systemContext
        },
        ...conversationHistory.map(msg => ({
          role: msg.role as 'user' | 'assistant',
          content: msg.content
        }))
      ];
      
      const response = await fetch("/api/openai-chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          messages, // Send full conversation history
          model: API_CONFIG.openai.model,
          temperature: 0.7,
          maxTokens: 300, // Reduced from 500 to encourage shorter responses
        }),
      });

      const data = await response.json();
      
      if (!response.ok) {
        // Surface the actual error from the API
        const errorMsg = data.error || `API error: ${response.status}`;
        console.error("Chat API error:", errorMsg);
        throw new Error(errorMsg);
      }

      return data.text || "I'm sorry, I couldn't generate a response. Please try again.";
    } catch (error: any) {
      console.error("Chat error:", error);
      // Re-throw with more context
      throw new Error(error.message || "Failed to connect to AI service");
    }
  }

  async identifyClubFromPhoto(base64Image: string): Promise<{
    brand: string;
    model: string;
    clubType: string;
    shaft?: { brand?: string; model?: string; flex?: string; type?: string };
    loft?: number;
    confidence: "high" | "medium" | "low";
  }> {
    try {
      const response = await fetch("/api/openai-chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          messages: [
            {
              role: "system",
              content: `You are a golf club identification expert. Analyze the photo and identify the golf club.
Return ONLY valid JSON with this exact structure (no markdown, no explanation):
{
  "brand": "brand name",
  "model": "model name",
  "clubType": "driver|3-wood|5-wood|7-wood|hybrid|2-iron|3-iron|4-iron|5-iron|6-iron|7-iron|8-iron|9-iron|pw|aw|gw|sw|lw|putter",
  "shaft": { "brand": "shaft brand", "model": "shaft model", "flex": "regular|stiff|x-stiff|senior|ladies", "type": "steel|graphite" },
  "loft": null,
  "confidence": "high|medium|low"
}
If you cannot identify a field, use null. The clubType must be one of the listed values.`,
            },
            {
              role: "user",
              content: [
                {
                  type: "text",
                  text: "Identify the golf club in this photo. Return only JSON.",
                },
                {
                  type: "image_url",
                  image_url: { url: base64Image },
                },
              ],
            },
          ],
          model: "gpt-4o",
          temperature: 0.2,
          maxTokens: 500,
        }),
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json();
      const text = data.text || data.content || "";

      // Parse JSON from response (strip markdown fences if present)
      const jsonStr = text.replace(/```json\n?/g, "").replace(/```\n?/g, "").trim();
      const parsed = JSON.parse(jsonStr);

      return {
        brand: parsed.brand || "Unknown",
        model: parsed.model || "Unknown",
        clubType: parsed.clubType || "7-iron",
        shaft: parsed.shaft || undefined,
        loft: parsed.loft || undefined,
        confidence: parsed.confidence || "low",
      };
    } catch (error: any) {
      console.error("Club photo identification failed:", error);
      throw new Error("Could not identify the club. Try a clearer photo with the club head and shaft label visible.");
    }
  }
}

export const aiService = new AIService();
